/**
 * "Add integration" picker — prototype/integrations-frames.js frame 13
 * (`set(12,...)`). Three picks: Google, Outlook (Beta), and Apple Calendar
 * shown disabled as "Coming soon" — discovered directly from the frame
 * markup, not called out in the README prose.
 */
import React, { useMemo } from "react";
import Modal from "../../theme/untitled/Modal/Modal";
import ProviderPickRow from "./ProviderPickRow";
import styles from "../IntegrationsPage.module.scss";

// Static — created once, not re-created per render.
const BETA_BADGE = { label: "Beta", color: "blueLight" };
const COMING_SOON_BADGE = { label: "Coming soon", color: "gray" };

// Describes the three picks; onClick is attached per-instance below since
// it needs the onSelectProvider prop — everything else here never changes.
const PICKS_CONFIG = [
  {
    provider: "google",
    title: "Google Calendar™",
    description: "Sync meetings and events from any of your Google calendars.",
  },
  {
    provider: "outlook",
    title: "Outlook Calendar™",
    description: "Sync events from Outlook and Microsoft 365 calendars.",
    badge: BETA_BADGE,
  },
  // No real Apple asset exists yet (the prototype reuses the Outlook glyph
  // as a demo placeholder) — pass a provider type with no
  // AvailableIntegrations match so the mark renders an empty disc rather
  // than showing the wrong brand's logo.
  // {
  //   provider: "apple",
  //   title: "Apple Calendar",
  //   description: "Not available yet — we will tell you when it is.",
  //   badge: COMING_SOON_BADGE,
  //   disabled: true,
  // },
];

const AddIntegrationSheet = ({ open, onClose, onSelectProvider }) => {
  // Memoized as a unit (not built inline in the .map() below) so each
  // row's onClick keeps a stable reference across renders — the same
  // concern useCallback exists for, just applied to a whole array at once,
  // matching RowMenu.jsx's items useMemo.
  const pickRows = useMemo(
    () =>
      PICKS_CONFIG.map((pick) => (
        <ProviderPickRow
          key={pick.provider}
          provider={pick.provider}
          title={pick.title}
          description={pick.description}
          badge={pick.badge}
          disabled={pick.disabled}
          onClick={
            pick.disabled ? undefined : () => onSelectProvider(pick.provider)
          }
        />
      )),
    [onSelectProvider]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add integration"
      size="lg"
      className={styles.addSheetPanel}
    >
      <p className={styles.emptyBody}>
        Pick where your data live. You will sign in to that account and choose
        what to sync.
      </p>
      <div className={styles.picks}>{pickRows}</div>
    </Modal>
  );
};

export default AddIntegrationSheet;
